import { createRootRoute, Link, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { PageHeader } from "@/components/page-header";
import { useT } from "@/glossary";
import { APP_CONTAINER_ID } from "@/lib/constants";
import { useUserPrefs } from "@/lib/stores/user-prefs";
import { useLangParam } from "@/lib/use-lang-param";
import { Onboarding } from "./-onboarding";

/* Keeps --app-scale in sync with the viewport so short screens (laptop
   browsers) see the whole frame without scrolling. See .app-frame in index.css. */
const useAppScale = () => {
  useEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const fitH = Number.parseFloat(
        getComputedStyle(root).getPropertyValue("--app-fit-h"),
      );
      const scale = fitH > 0 ? Math.min(1, window.innerHeight / fitH) : 1;
      root.style.setProperty("--app-scale", scale.toFixed(4));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
};

const RootComponent = () => {
  useLangParam();
  useAppScale();
  const prefs = useUserPrefs();

  return (
    <div className="flex h-dvh items-center justify-center overflow-hidden">
      <Toaster position="top-center" />
      <div className="app-frame">
        <div id={APP_CONTAINER_ID}>
          <PageHeader />
          {prefs ? (
            <main className="mt-(--main-mt) flex min-h-0 flex-1 flex-col">
              <Outlet />
            </main>
          ) : (
            <Onboarding />
          )}
        </div>
      </div>
    </div>
  );
};

const NotFound = () => {
  const t = useT();
  return (
    <div className="flex flex-col items-center gap-6 p-8 text-center">
      <h1 className="font-bold text-2xl">{t.notFound.title}</h1>
      <p>{t.notFound.description}</p>
      <Link
        to="/"
        className="rounded border border-foreground px-4 py-2 text-center"
      >
        {t.common.home}
      </Link>
    </div>
  );
};

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});
